import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import {
  getTrainerDataFromLocalStorage,
  getTrainerId,
  setTrainerDataInLocalStorage,
} from "../utils/helpers";
import moment from "moment";
import { authService } from "./services";

const axiosInstance = axios.create({
  timeout: 10000,
});

const thirdPartyAxiosInst = axios.create({
  timeout: 20000,
});

axiosInstance.interceptors.request.use(
  (requestConfig) => {
    const trainerId = getTrainerId();
    const trainerToken = getTrainerDataFromLocalStorage("token");
    if (!requestConfig.headers["x-user-id"]) {
      requestConfig.headers["x-user-id"] = trainerId;
    }
    requestConfig.headers["x-authorization"] = trainerToken || "";
    requestConfig.headers["time-zone"] = `GMT${moment().format("Z")}`;

    if (!requestConfig.headers["Content-Type"]) {
      requestConfig.headers["Content-Type"] = "application/json";
    }

    return requestConfig;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const { response } = error;
    if (response) {
      if (response?.status === 401) {
        try {
          const trainerRefreshToken =
            getTrainerDataFromLocalStorage("refreshToken");
          if (
            !trainerRefreshToken ||
            (error.config as any).retryRequest === true
          ) {
            throw new Error("");
          }
          (error.config as any).retryRequest = true;

          const { data } = (await makeRequest(
            authService.refreshToken(trainerRefreshToken),
            {
              throwErrorOnFailure: true,
            }
          )) as AxiosResponse;
          const { idToken } = data || {};
          if (idToken) {
            setTrainerDataInLocalStorage([
              { key: "token", value: new String(idToken).toString() },
            ]);

            (error.config as any).retryRequest = undefined;
            if (error.config) {
              error.config.headers["x-authorization"] = idToken;
            }
            return axios.request(error.config!);
          } else {
            throw new Error("No id token found");
          }
        } catch {
          localStorage.clear();
          window.location.replace("/login");
        }
      }
    }
    return Promise.reject(error);
  }
);

const makeRequest = async (
  reqParams: AxiosRequestConfig,
  {
    onSuccess,
    onError,
    throwErrorOnFailure = true,
  }: {
    onSuccess?: CallableFunction;
    onError?: CallableFunction;
    throwErrorOnFailure?: boolean;
  } = {}
) => {
  try {
    const response = await axiosInstance(reqParams);
    onSuccess && onSuccess(response);
    return response;
  } catch (error: any) {
    onError && onError(error.response);
    if (throwErrorOnFailure) {
      throw error;
    }
  }
};

export const makeThirdPartyRequest = async (
  reqParams: AxiosRequestConfig,
  {
    onSuccess,
    onError,
    throwErrorOnFailure = true,
  }: {
    onSuccess?: CallableFunction;
    onError?: CallableFunction;
    throwErrorOnFailure?: boolean;
  } = {}
) => {
  try {
    const response = await thirdPartyAxiosInst(reqParams);
    onSuccess && onSuccess(response);
    return response;
  } catch (error: any) {
    onError && onError(error.response);
    if (throwErrorOnFailure) {
      throw error;
    }
  }
};

export default makeRequest;
